import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useScoreTracking } from "../../context/ScoreTrackingContext";
import {
  useGetLessonQuery,
  useCompleteLessonMutation,
  useSkipLessonMutation,
  useSubmitCardCompletionMutation,
} from "../../store/api/postgresCourseApi";
import { LessonPlayerState } from "./types";
import { isCardNonInteractive } from "../../utils/cardUtils";
import { postgresUserApi } from "../../store/api/postgresUserApi";
import { useImagePrefetch } from "../../hooks/useImagePrefetch";
import { useAuth } from "../../supabase/AuthProvider";
import { supabase } from "../../supabase/supabaseClient";
import { getCurrentSession } from "../../supabase/authService";
import { useRefreshLessonCompletionContextMutation, type LessonCompletionContext } from "../../store/api/masterCourseApi";
import { useResolveCourseCertificateMutation, type CourseCertificateStatus } from "../../store/api/postgresCertificatesApi";
import {
  readCompletionAttempt, persistCompletionAttempt, clearCompletionAttempt, confirmsAttempt,
  completionSaveError, asCompletionError, RECOVERY_TTL, type CompletionAttempt, type CompletionSaveError,
} from "./lessonCompletionRecovery";

const SOFT_LOCK_FALLBACK_MESSAGE =
  "Thank you for your time and dedication to Module 1 of this course. The remaining half of this module will be unlocked on Tuesday 18th November, with content specifically customised to your organisation.";

const createInitialLessonPlayerState = (): LessonPlayerState => ({
  currentCardIndex: 0,
  showLessonContent: false,
  showCongratulations: false,
  showCertificate: false,
  isGeneratingCertificate: false,
  score: 0,
  totalScore: 0,
  completedCards: new Set(),
  canProceed: false,
  completionVariant: "completed",
});

export const useLessonPlayerHook = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading, sessionValidation } = useAuth();
  const { data: profile } = postgresUserApi.useGetCurrentUserQuery(undefined, { skip: !user });
  const [attempt, setAttempt] = useState<CompletionAttempt | null>(readCompletionAttempt);
  const attemptRef = useRef(attempt);
  // Only a save started on this mounted unit uses the ordinary completing UI.
  // Stored attempts and attempts left behind by navigation need recovery controls.
  const [inSessionAttemptId, setInSessionAttemptId] = useState<string | null>(null);
  const [recoveryError, setRecoveryError] = useState<CompletionSaveError | null>(() => attempt?.lastFailureStatus === 401 ? completionSaveError({ status: 401 }) : null);
  const [recoveryPersistent, setRecoveryPersistent] = useState(true);
  const persistentRef = useRef(true);
  const [completionWasReview, setCompletionWasReview] = useState(false);
  const [completionContext, setCompletionContext] = useState<LessonCompletionContext | null>(null);
  const [certificateStatus, setCertificateStatus] = useState<CourseCertificateStatus>("unknown");
  const [refreshContext] = useRefreshLessonCompletionContextMutation();
  const [resolveCertificate] = useResolveCourseCertificateMutation();
  const mounted = useRef(true);
  const generation = useRef(0);
  const busy = useRef(false);
  const restoredSaved = useRef<string | null>(null);
  const current = useRef({ lessonId, userId: user?.id, organisationId: profile?.organisation_id });
  current.current = { lessonId, userId: user?.id, organisationId: profile?.organisation_id };
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; generation.current += 1; };
  }, []);

  // State management
  const [state, setState] = useState<LessonPlayerState>(() => createInitialLessonPlayerState());

  // Lesson completion loading state
  const [isLessonCompleting, setIsLessonCompleting] = useState(false);

  const [softLockMessage, setSoftLockMessage] = useState<string | null>(null);
  const [showSoftLockModal, setShowSoftLockModal] = useState(false);

  const openSoftLockModal = (message?: string | null) => {
    const trimmed = typeof message === "string" ? message.trim() : "";
    setSoftLockMessage(trimmed || SOFT_LOCK_FALLBACK_MESSAGE);
    setShowSoftLockModal(true);
  };

  const closeSoftLockModal = () => {
    setShowSoftLockModal(false);
  };

  // Score tracking context (mistakes gathered during interactions)
  const { getFinalScores, getMistakes, isCardCompleted, resetLessonScores } =
    useScoreTracking();

  // Manual refetch trigger
  const [triggerRefetchUser] = postgresUserApi.useLazyGetCurrentUserQuery();

  // Refs
  const lessonViewRef = useRef<HTMLDivElement>(null);
  const certificateRef = useRef<HTMLDivElement>(null);
  const previousLessonIdRef = useRef<string | undefined>(lessonId);

  // API calls
  const {
    data: queriedLesson,
    isLoading,
    error,
  } = useGetLessonQuery(lessonId || "", {
    skip: !lessonId,
  });
  // RTK can retain the previous route's data while fetching the new unit.
  const lesson = !error && queriedLesson?.id === lessonId ? queriedLesson : undefined;
  const lessonErrorCode =
    typeof asCompletionError(error)?.data?.error_code === "string"
      ? asCompletionError(error).data?.error_code
      : null;
  useImagePrefetch(lesson?.cards);
  const [completeLessonMutation] = useCompleteLessonMutation();
  const [skipLessonMutation] = useSkipLessonMutation();
  const [submitCardCompletionMutation] = useSubmitCardCompletionMutation();

  useEffect(() => {
    if (previousLessonIdRef.current === lessonId) {
      return;
    }

    previousLessonIdRef.current = lessonId;
    setInSessionAttemptId(null);
    resetLessonScores();
    generation.current += 1;
    setCompletionContext(null);
    setCertificateStatus("unknown");
    setRecoveryError(null);
    setShowSoftLockModal(false);
    setSoftLockMessage(null);
    setState(createInitialLessonPlayerState());
  }, [lessonId, resetLessonScores]);

  // Computed values
  const currentCard = lesson?.cards[state.currentCardIndex];
  const isLastCard = state.currentCardIndex === (lesson?.cards.length || 0) - 1;
  const isFirstCard = state.currentCardIndex === 0;
  const totalCards = lesson?.cards.length || 0;
  const progressPercentage =
    totalCards > 0 ? ((state.currentCardIndex + 1) / totalCards) * 100 : 0;

  useEffect(() => {
    if (!error) {return;}
    if (lessonErrorCode === "course_retired") {
      setShowSoftLockModal(false);
      setSoftLockMessage(null);
      return;
    }
    const status = asCompletionError(error)?.status;
    if (status === 403 || status === 423) {
      const message =
        typeof asCompletionError(error)?.data?.message === "string"
          ? asCompletionError(error).data?.message
          : null;
      openSoftLockModal(message || "This unit is not currently available. Check your course access or return to your courses.");
    }
  }, [error, lessonErrorCode]);

  // Handle card state changes
  const handleCardStateChange = useCallback(
    async (isCompleted: boolean, canProceed: boolean) => {
      console.log("[LessonPlayer] handleCardStateChange called, isCompleted:", isCompleted, "canProceed:", canProceed, "cardId:", currentCard?.id);
      setState((prev) => ({
        ...prev,
        canProceed,
      }));

      // When a scored card is completed, immediately submit per-card completion
      if (isCompleted && currentCard) {
        const scoredTypes = new Set([
          "knowledge_check",
          "match_exercise",
          "drag_and_drop_sorter",
          "data_visualization",
          "sequence_sort",
        ]);
        // if card is interactive and scored types + added condition that it does not call api again when user navigates to previous and agian answer the cards
        if (
          scoredTypes.has(currentCard.type) &&
          !isCardCompleted(currentCard?.id)
        ) {
          const cardId = currentCard.id;
          // Determine score from card content's max_score
          const content = currentCard.content as {max_score?: number};
          const score =
            typeof content?.max_score === "number" ? content.max_score : 0;
          const mistakes = getMistakes(cardId) || 0;

          try {
            await submitCardCompletionMutation({
              cardId,
              data: { score, mistakes },
            }).unwrap();

          } catch (error) {
            console.error("Failed to submit card completion:", error);
          }
        }
      }
    },
    // Dependencies explicitly listed - callback recreates when card changes
    // Child hooks use useStableCallback to handle the instability
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentCard?.id, currentCard?.type, currentCard?.content, getMistakes, isCardCompleted, submitCardCompletionMutation]
  );

  // Update canProceed when card changes - only set to true for non-interactive cards
  // Interactive cards will set their own canProceed state via handleCardStateChange
  useEffect(() => {
    if (currentCard) {
      const isNonInteractive = isCardNonInteractive(currentCard.type);
      console.log("[LessonPlayer] Card changed to:", currentCard.type, "isNonInteractive:", isNonInteractive);
      // Only set canProceed to true for non-interactive cards
      // Interactive cards start with canProceed: false and will update via handleCardStateChange
      if (isNonInteractive) {
        console.log("[LessonPlayer] Setting canProceed: true for non-interactive card");
        setState((prev) => ({
          ...prev,
          canProceed: true,
        }));
      } else {
        console.log("[LessonPlayer] Setting canProceed: false until interactive card completion");
        setState((prev) => ({
          ...prev,
          canProceed: false,
        }));
      }
    }
  }, [currentCard]);

  // Actions
  const startLesson = () => {
    if (!lesson) {return;}
    // Reset any previous lesson scores when starting a new lesson
    resetLessonScores();

    setState((prev) => ({
      ...prev,
      showLessonContent: true,
    }));

    setTimeout(() => {
      lessonViewRef.current?.scrollIntoView({ behavior: "auto" });
    }, 100);
  };

  const nextCard = () => {
    if (!lesson) {return;}
    console.log("[LessonPlayer] nextCard called, isLastCard:", isLastCard, "currentIndex:", state.currentCardIndex);
    if (isLastCard) {
      completeLesson();
      return;
    }

    setState((prev) => ({
      ...prev,
      currentCardIndex: prev.currentCardIndex + 1,
    }));
    console.log("[LessonPlayer] Moving to card index:", state.currentCardIndex + 1);
  };

  const previousCard = () => {
    if (isFirstCard) {return;}

    setState((prev) => ({
      ...prev,
      currentCardIndex: prev.currentCardIndex - 1,
    }));
  };

  const completeCard = (cardId: string, cardScore = 0) => {
    setState((prev) => ({
      ...prev,
      completedCards: new Set([...Array.from(prev.completedCards), cardId]),
      score: prev.score + cardScore,
      totalScore: prev.totalScore + cardScore,
    }));
  };

  const replaceAttempt = (value: CompletionAttempt | null) => {
    attemptRef.current = value;
    if (mounted.current) {setAttempt(value);}
  };

  const isCurrent = (value: CompletionAttempt, epoch: number) => mounted.current && generation.current === epoch &&
    current.current.lessonId === value.lessonId && current.current.userId === value.userId &&
    current.current.organisationId === value.organisationId;

  const refreshSavedProgress = async (value: CompletionAttempt, epoch: number) => {
    try {
      const context = await refreshContext({ lessonId: value.lessonId,
        identity: { userId: value.userId, organisationId: value.organisationId } }).unwrap();
      if (!isCurrent(value, epoch)) {return;}
      setCompletionWasReview(value.wasReview);
      setCompletionContext(context);
      setRecoveryError(null);
      // No score payload is needed once the unit is confirmed and progress has refreshed.
      clearCompletionAttempt(value.attemptId);
      if (attemptRef.current?.attemptId === value.attemptId) {replaceAttempt(null);}
      setState((prev) => ({ ...prev, showCongratulations: true,
        completionVariant: value.operation === "skip" ? "skipped" : "completed" }));
      if (context.master.isCompleted) {
        if (context.certificateEntitled === false) {
          setCertificateStatus("not-entitled");
        } else if (context.certificateEntitled === null) {
          setCertificateStatus("unknown");
        } else {
          setCertificateStatus("checking");
          try {
            const result = await resolveCertificate({ masterCourseId: context.master.id,
              identity: { userId: value.userId, organisationId: value.organisationId } }).unwrap();
            if (isCurrent(value, epoch)) {setCertificateStatus(result.status);}
          } catch (cause: unknown) {
            const certificateError = asCompletionError(cause);
            if (isCurrent(value, epoch)) {setCertificateStatus(certificateError?.status === 403 ? "not-entitled" : "unknown");}
          }
        }
      }
    } catch (cause: unknown) {
      const refreshError = asCompletionError(cause);
      if (!isCurrent(value, epoch)) {return;}
      if (refreshError?.data?.error === "COMPLETION_IDENTITY_CHANGED") {
        clearCompletionAttempt(value.attemptId);
        if (attemptRef.current?.attemptId === value.attemptId) {replaceAttempt(null);}
        setRecoveryError(completionSaveError(refreshError));
      } else {
        setRecoveryError({ kind: "retry", message: "This unit has saved, but we couldn't refresh your course progress. Please try again." });
      }
    }
  };

  const submitAttempt = async (value: CompletionAttempt, epoch: number) => {
    const identity = { userId: value.userId, organisationId: value.organisationId };
    const send = () => value.operation === "skip"
      ? skipLessonMutation({ lessonId: value.lessonId, identity }).unwrap()
      : completeLessonMutation({ lessonId: value.lessonId, data: value.data!, identity }).unwrap();
    try {
      let response;
      try {
        response = await send();
      } catch (cause: unknown) {
        const firstError = asCompletionError(cause);
        if (firstError?.status !== 401 || !isCurrent(value, epoch) || !persistentRef.current) {throw firstError;}
        // The existing auth guard may remount this player during SDK refresh.
        const retained = { ...value, lastFailureStatus: 401 as const };
        if (readCompletionAttempt()?.attemptId === value.attemptId) {persistCompletionAttempt(retained);}
        replaceAttempt(retained);
        // Exactly one refresh per user-triggered submission, using the existing SDK auth flow.
        const refreshed = await supabase.auth.refreshSession();
        if (refreshed.error || !refreshed.data.session) {throw firstError;}
        if (refreshed.data.session.user.id !== value.userId) {
          throw Object.assign(new Error("Completion identity changed"), { data: { error: "COMPLETION_IDENTITY_CHANGED" } });
        }
        if (!isCurrent(value, epoch)) {throw firstError;}
        response = await send();
      }
      if (!confirmsAttempt(response, value)) {throw Object.assign(new Error("Completion response could not be confirmed"), { status: "CUSTOM_ERROR" });}
      // A late response may settle its OWN entry, never another entry or the new lesson's scores.
      if (attemptRef.current?.attemptId !== value.attemptId) {return;}
      const stored = readCompletionAttempt();
      if ((stored && stored.attemptId !== value.attemptId) || (!mounted.current && !stored)) { return; }
      const saved: CompletionAttempt = { ...value, phase: "saved", data: undefined, lastFailureStatus: undefined };
      const persisted = persistCompletionAttempt(saved);
      replaceAttempt(saved);
      if (!isCurrent(value, epoch)) {return;}
      restoredSaved.current = value.attemptId;
      persistentRef.current = persisted;
      setRecoveryPersistent(persisted);
      setCompletionWasReview(value.wasReview);
      resetLessonScores();
      triggerRefetchUser();
      setState((prev) => ({ ...prev, showCongratulations: true,
        completionVariant: value.operation === "skip" ? "skipped" : "completed" }));
      if (typeof response.soft_lock_message === "string" && response.soft_lock_message.trim()) {
        openSoftLockModal(response.soft_lock_message);
      }
      await refreshSavedProgress(saved, epoch);
    } catch (cause: unknown) {
      const saveError = asCompletionError(cause);
      if (!isCurrent(value, epoch)) {return;}
      const mapped = completionSaveError(saveError);
      if (mapped.kind === "identity") {
        clearCompletionAttempt(value.attemptId);
        if (attemptRef.current?.attemptId === value.attemptId) {replaceAttempt(null);}
      }
      if ((saveError?.data?.error === "LESSON_SOFT_LOCKED" || saveError?.data?.error_code === "lesson_soft_locked")) {openSoftLockModal(saveError.data.message);}
      if (mapped.kind === "sign-in" && !persistentRef.current) {
        mapped.message = "Sign-in is needed to save this attempt. Browser storage is unavailable; keep this page open because it cannot preserve the attempt across sign-in navigation.";
      }
      setRecoveryError(mapped);
    }
  };

  const runRecovery = async () => {
    const value = attemptRef.current;
    if (!value || busy.current || (recoveryError?.retryAt || 0) > Date.now()) {return;}
    if (value.expiresAt <= Date.now()) {
      clearCompletionAttempt(value.attemptId);
      replaceAttempt(null);
      setRecoveryError({ kind: "identity", message: "The local recovery attempt has expired. Check your saved course progress before continuing." });
      return;
    }
    if (current.current.lessonId !== value.lessonId) {
      navigate(`/lesson/${value.lessonId}`);
      return;
    }
    busy.current = true;
    setIsLessonCompleting(true);
    const epoch = generation.current;
    try {
      if (value.phase === "saved") {await refreshSavedProgress(value, epoch);}
      else {await submitAttempt(value, epoch);}
    } finally {
      busy.current = false;
      if (mounted.current) {setIsLessonCompleting(false);}
    }
  };

  const beginAttempt = async (operation: "complete" | "skip") => {
    if (busy.current || !lessonId || !lesson) {return;}
    const existing = attemptRef.current;
    if (existing?.phase === "pending") {
      if (existing.lessonId === lessonId) {await runRecovery();}
      else {setRecoveryError({ kind: "retry", message: "Return to the other unit to recover or discard its completed attempt before saving another unit." });}
      return;
    }
    if (!user || profile?.id !== user.id || profile.organisation_id === undefined || authLoading || sessionValidation !== "ok") {
      setRecoveryError({ kind: "retry", message: "We couldn't confirm your account details. Keep this page open and try saving again." });
      return;
    }
    const now = Date.now();
    const offset = new Date().getTimezoneOffset();
    const cardIds = new Set(lesson.cards.map((card) => card.id));
    const value: CompletionAttempt = {
      version: 1, attemptId: crypto.randomUUID(), userId: user.id, organisationId: profile.organisation_id,
      lessonId, operation, phase: "pending", createdAt: now, expiresAt: now + RECOVERY_TTL,
      wasReview: lesson.learner_progress?.is_completed === true && !lesson.learner_progress.skipped_at,
      data: { card_scores: operation === "skip" ? [] : getFinalScores().card_scores
        .filter((score) => cardIds.has(score.card_id)).map((score) => ({ ...score })),
      local_date: new Date(now - offset * 60_000).toISOString().slice(0, 10),
      local_offset_minutes: offset, time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || "" },
    };
    if (existing) {clearCompletionAttempt(existing.attemptId);}
    persistentRef.current = persistCompletionAttempt(value);
    setRecoveryPersistent(persistentRef.current);
    setInSessionAttemptId(value.attemptId);
    replaceAttempt(value);
    setRecoveryError(null);
    setCompletionContext(null);
    await runRecovery();
  };
  const completeLesson = () => beginAttempt("complete");
  const skipLesson = () => beginAttempt("skip");

  const discardRecovery = () => {
    if (busy.current || !attemptRef.current) {return;}
    const value = attemptRef.current;
    clearCompletionAttempt(value.attemptId);
    replaceAttempt(null);
    setRecoveryError(null);
    if (value.lessonId === lessonId) {
      resetLessonScores();
      setState(createInitialLessonPlayerState());
    }
  };
  const returnToRecovery = () => {
    if (attemptRef.current) {navigate(`/lesson/${attemptRef.current.lessonId}`);}
  };
  const signInForRecovery = async () => {
    const value = attemptRef.current;
    if (!value || !recoveryPersistent) {return;}
    // A successful explicit sign-in returns to a pending attempt with a retry control.
    if (!persistCompletionAttempt({ ...value, lastFailureStatus: undefined })) {return;}
    const session = await getCurrentSession();
    if (session && session.user.id !== value.userId) {return;}
    const returnTo = `${window.location.origin}/lesson/${value.lessonId}`;
    window.location.assign(`${process.env.REACT_APP_APP_HOME_URL || "https://app.pedagogue.io"}/login?return_to=${encodeURIComponent(returnTo)}`);
  };

  const previousIdentity = useRef<string | null>(null);
  useEffect(() => {
    const value = attemptRef.current;
    const knownOrg = profile?.id === user?.id ? profile?.organisation_id : undefined;
    const verifiedKey = user && knownOrg !== undefined ? `${user.id}:${knownOrg}` : null;
    const changed = Boolean(verifiedKey && previousIdentity.current && verifiedKey !== previousIdentity.current);
    const foreignAttempt = Boolean(value && user && (value.userId !== user.id || (knownOrg !== undefined && value.organisationId !== knownOrg)));
    if (changed || foreignAttempt) {
      if (value) {clearCompletionAttempt(value.attemptId);}
      attemptRef.current = null;
      setAttempt(null);
      setRecoveryError(null);
      setCompletionContext(null);
      setCertificateStatus("unknown");
      generation.current += 1;
      resetLessonScores();
      setState(createInitialLessonPlayerState());
    }
    if (verifiedKey) {previousIdentity.current = verifiedKey;}
  }, [user, profile?.id, profile?.organisation_id, resetLessonScores]);

  const recoveryRunner = useRef(runRecovery);
  recoveryRunner.current = runRecovery;

  // A confirmed save in another unit needs no retry. Keep own-unit GET recovery intact.
  useEffect(() => {
    if (attempt?.phase !== "saved" || attempt.lessonId === lessonId) {return;}
    clearCompletionAttempt(attempt.attemptId);
    if (attemptRef.current?.attemptId === attempt.attemptId) {
      attemptRef.current = null;
      setAttempt(null);
      setRecoveryError(null);
    }
  }, [attempt, lessonId]);

  // Restoring a confirmed save performs GET recovery only; restoring a pending one awaits a click.
  useEffect(() => {
    if (attempt?.phase !== "saved" || attempt.lessonId !== lessonId || profile?.id !== user?.id ||
      attempt.userId !== user?.id || attempt.organisationId !== profile?.organisation_id ||
      restoredSaved.current === attempt.attemptId) {return;}
    if (busy.current) {return;}
    restoredSaved.current = attempt.attemptId;
    setCompletionWasReview(attempt.wasReview);
    setState((prev) => ({ ...prev, showCongratulations: true,
      completionVariant: attempt.operation === "skip" ? "skipped" : "completed" }));
    void recoveryRunner.current();
  }, [attempt, lessonId, profile?.id, profile?.organisation_id, user?.id]);

  const recoveryAttempt = user && profile?.id === user.id && attempt?.userId === user.id &&
    attempt.organisationId === profile.organisation_id ? attempt : null;
  const recovery = { recoveryAttempt, recoveryRestored: !!recoveryAttempt && recoveryAttempt.attemptId !== inSessionAttemptId,
    recoveryError, recoveryPersistent, runRecovery: () => attemptRef.current ? runRecovery() : completeLesson(), discardRecovery,
    returnToRecovery, signInForRecovery, completionContext, completionWasReview, certificateStatus,
    refreshCourseProgress: () => {
      if (attemptRef.current?.phase === "saved") {void runRecovery();}
    },
    refreshCertificateStatus: async () => {
      if (!completionContext?.master.isCompleted || !user || profile?.id !== user.id || profile.organisation_id === undefined) {return;}
      const epoch = generation.current;
      const expectedLesson = lessonId;
      setCertificateStatus("checking");
      try {
        const result = await resolveCertificate({ masterCourseId: completionContext.master.id,
          identity: { userId: user.id, organisationId: profile.organisation_id } }).unwrap();
        if (mounted.current && generation.current === epoch && current.current.lessonId === expectedLesson) {setCertificateStatus(result.status);}
      } catch (cause: unknown) {
        const certificateError = asCompletionError(cause);
        if (mounted.current && generation.current === epoch && current.current.lessonId === expectedLesson) {setCertificateStatus(certificateError?.status === 403 ? "not-entitled" : "unknown");}
      }
    },
  };

  const handleViewCertificate = () => {
    setState((prev) => ({
      ...prev,
      showCertificate: true,
    }));
  };

  const handleDownloadCertificate = async () => {
    if (!certificateRef.current || !lesson) {return;}

    setState((prev) => ({
      ...prev,
      isGeneratingCertificate: true,
    }));

    try {
      // Certificate generation logic would go here
      // For now, just simulate the process
      await new Promise((resolve) => setTimeout(resolve, 2000));
      console.log("Certificate downloaded");
    } catch (error) {
      console.error("Error generating certificate:", error);
    } finally {
      setState((prev) => ({
        ...prev,
        isGeneratingCertificate: false,
      }));
    }
  };

  const handleShareOnLinkedIn = () => {
    const certificateUrl = window.location.href;
    const shareText = `I've completed the ${lesson?.title} on Pedagogue! Check out my certificate.`;
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      certificateUrl
    )}&summary=${encodeURIComponent(shareText)}`;
    window.open(linkedInUrl, "_blank");
  };

  const handleShareCertificate = () => {
    if (navigator.share) {
      navigator
        .share({
          title: `${lesson?.title} Certificate`,
          text: `I've completed the ${lesson?.title} on Learnly! Check out my certificate.`,
          url: window.location.href,
        })
        .catch(console.error);
    } else {
      navigator.clipboard
        .writeText(window.location.href)
        .then(() => alert("Certificate link copied to clipboard!"))
        .catch(console.error);
    }
  };

  const goHome = () => {
    // Reset lesson scores when going home
    resetLessonScores();
    navigate("/");
  };

  // Card type validation
  const isCardTypeHandled = (type: string): boolean => {
    return [
      "quick_question",
      "video",
      "knowledge_check",
      "match_exercise",
      "drag_and_drop_sorter",
      "image_hotspot",
      "data_visualization",
      "sequence_sort",
      "information",
      "data_table",
      "flashcard",
      "quick_recap",
      "learning_outcomes",
      "reveal_text_strip",
      "peda",
    ].includes(type);
  };

  // Error handling with useEffect
  // TEMPORARILY DISABLED: Causing immediate redirect on Cloud Run cold starts
  // useEffect(() => {
  //   if (error) {
  //     console.error("[LessonPlayer] Lesson query error:", error);
  //     navigate("/lessonNotFound/unknown");
  //   }
  // }, [error, navigate]);

  const queryStatus = asCompletionError(error)?.status;
  const transientQueryFailure = typeof queryStatus === "number" ? queryStatus === 408 || queryStatus === 429 || queryStatus >= 500 :
    ["FETCH_ERROR", "TIMEOUT_ERROR", "PARSING_ERROR"].includes(String(queryStatus));
  const showSavedFeedback = state.showCongratulations && (!error || transientQueryFailure);

  if (error && !showSavedFeedback) {
    return {
      ...recovery,
      loading: true,
      lesson: undefined,
      currentCard: undefined,
      isLoading: true,
      showLessonContent: false,
      showCongratulations: false,
      showCertificate: false,
      isGeneratingCertificate: false,
      lessonViewRef,
      certificateRef,
      score: 0,
      totalCards: 0,
      progressPercentage: 0,
      currentCardIndex: 1,
      canProceed: false,
      isLastCard: false,
      isFirstCard: true,
      startLesson: () => {},
      nextCard: () => {},
      previousCard: () => {},
      completeCard: () => {},
      skipLesson: async () => {},
      completionVariant: "completed" as const,
      handleCardStateChange: () => {},
      handleViewCertificate: () => {},
      handleDownloadCertificate: async () => {},
      handleShareOnLinkedIn: () => {},
      handleShareCertificate: () => {},
      goHome: () => {},
      isLessonCompleting,
      isCardTypeHandled: () => false,
      showSoftLockModal,
      softLockMessage,
      closeSoftLockModal,
      openSoftLockModal,
      lessonErrorCode,
    };
  }

  if (!lesson) {
    return {
      ...recovery,
      loading: isLoading && !showSavedFeedback,
      lesson: undefined,
      currentCard: undefined,
      isLoading: isLoading && !showSavedFeedback,
      showLessonContent: false,
      showCongratulations: showSavedFeedback,
      showCertificate: false,
      isGeneratingCertificate: false,
      lessonViewRef,
      certificateRef,
      score: 0,
      totalCards: 0,
      progressPercentage: 0,
      currentCardIndex: 1,
      canProceed: false,
      isLastCard: false,
      isFirstCard: true,
      startLesson: () => {},
      nextCard: () => {},
      previousCard: () => {},
      completeCard: () => {},
      skipLesson: async () => {},
      completionVariant: state.completionVariant,
      handleCardStateChange: () => {},
      handleViewCertificate: () => {},
      handleDownloadCertificate: async () => {},
      handleShareOnLinkedIn: () => {},
      isLessonCompleting,
      handleShareCertificate: () => {},
      goHome: () => {},
      isCardTypeHandled: () => false,
      showSoftLockModal,
      softLockMessage,
      closeSoftLockModal,
      openSoftLockModal,
      lessonErrorCode,
    };
  }

  return {
    ...recovery,
    // Data
    lesson,
    currentCard,
    isLoading,

    // State
    ...state,

    // Computed values
    isLastCard,
    isFirstCard,
    totalCards,
    progressPercentage,
    canProceed: state.canProceed,

    // Refs
    lessonViewRef,
    certificateRef,

    // Actions
    startLesson,
    nextCard,
    previousCard,
    completeCard,
    skipLesson,
    handleCardStateChange,
    handleViewCertificate,
    handleDownloadCertificate,
    handleShareOnLinkedIn,
    handleShareCertificate,
    goHome,
    isCardTypeHandled,

    // Lesson completion loading state
    isLessonCompleting,

    // Soft lock modal controls
    showSoftLockModal,
    softLockMessage,
    closeSoftLockModal,
    openSoftLockModal,
    lessonErrorCode,
  };
};
